import React, { useEffect, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import { useSelector } from "react-redux";
import { API_URL } from "../../constants/urls";
import { useLocation, NavLink } from "react-router-dom";
import rain from "../../Static/Img/Devices&channels/speedometer.gif";
import { download } from "./DeviceFunctions";
import DatePicker from "react-datepicker";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import { Line } from "react-chartjs-2";
import Dropdown from "react-bootstrap/Dropdown";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { io } from "socket.io-client";
import "./DeviceChannels.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function calculateTimeDifference(mongoTime) {
  const mongoDate = new Date(mongoTime);
  const currentDate = new Date();
  if (isNaN(mongoDate.getTime())) return "Invalid timestamp.";
  if (mongoDate > currentDate) return "In future.";
  const diff = currentDate - mongoDate;
  const minute = 1000 * 60;
  const hour = minute * 60;
  const day = hour * 24;
  const month = day * 30.44;
  const year = day * 365.25;

  if (diff / year >= 1) return `${Math.floor(diff / year)}y ago`;
  if (diff / month >= 1) return `${Math.floor(diff / month)}mo ago`;
  if (diff / day >= 1) return `${Math.floor(diff / day)}d ago`;
  if (diff / hour >= 1) return `${Math.floor(diff / hour)}h ago`;
  return `${Math.floor(diff / minute)}m ago`;
}

export const options = {
  maintainAspectRatio: false,
  tension: 0.3,
  plugins: {
    legend: { position: "top" },
    tooltip: {
      callbacks: {
        title: (t) => {
          const i = t[0];
          const d = i.dataset.date ? i.dataset.date[i.dataIndex] : "";
          const v = i.formattedValue;
          const l = i.dataset.label || "";
          return `${l}: ${v} (Last Updated: ${d})`;
        },
        label: () => null,
      },
      backgroundColor: "#fff",
      titleColor: "#262b40",
      bodyColor: "grey",
      borderColor: "lightGrey",
      borderWidth: 1,
      displayColors: false,
      titleFont: { weight: "normal", size: 16 },
    },
  },
};

const labels = Array.from({ length: 50 }, (_, i) => `${i + 1}`);

const Devices = () => {
  const { user } = useSelector((state) => ({ ...state.auth }));
  const location = useLocation();
  const pastDate = new Date();
  pastDate.setDate(pastDate.getDate() - 365);
  const nDate = new Date();
  nDate.setDate(nDate.getDate() - 30);

  const [channels, setChannels] = useState([]);
  const [buttons, setButtons] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [pushButtons, setPushButtons] = useState([]);
  const [file, setFile] = useState([]);
  const [refreshData, setRefreshData] = useState(false);
  const [startDate, setStartDate] = useState(nDate);
  const [endDate, setEndDate] = useState(new Date());

  const deviceId = location.state.deviceId;

  // 🔹 MQTT publish "handshake"
  const publishHandshake = async () => {
    try {
      const res = await fetch(`${API_URL}/mqtt/publish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, message: "handshake" }),
      });
      const data = await res.json();
      console.log("🚀 Handshake published:", data);
    } catch (err) {
      console.error("Publish error:", err);
    }
  };

  useEffect(() => {
    const socket = io(API_URL.replace("/api", ""));
    socket.emit("joinDeviceRoom", deviceId);

    // Subscribe this deviceId
    fetch(`${API_URL}/mqtt/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId }),
    })
      .then((res) => res.json())
      .then((data) => console.log("📡 Subscribed to:", data.topic))
      .catch((err) => console.error("Subscribe error:", err));

    socket.on("mqttMessage", (data) => {
      console.log("📩 MQTT Message Received:", data);
      if (data.deviceId !== deviceId) return;

      // Step 1️⃣ — try parsing JSON safely
      let payload = null;
      try {
        payload = JSON.parse(data.message);
      } catch (err) {
        console.warn("⚠️ Non-JSON MQTT message received:", data.message);
      }

      // ✅ Update fb1/fb2 states
      if (payload && payload.do) {
        setButtons((prevButtons) =>
          prevButtons.map((btn, index) => {
            const doData = payload.do[index + 1];
            if (doData) {
              return {
                ...btn,
                fb1: doData.fb1,
                fb2: doData.fb2,
              };
            }
            return btn;
          })
        );
      }

      // ✅ Update DI indicators
      if (payload && payload.di) {
        const diData = payload.di;
        setIndicators((prev) =>
          prev.map((indicator, index) => {
            const diValue = diData[index + 1];
            return {
              ...indicator,
              value: diValue,
            };
          })
        );
      }

      // ✅ Handle simple "doX on/off"
      if (!payload && typeof data.message === "string") {
        const match = data.message.match(/^do(\d+)\s+(on|off)$/i);
        if (match) {
          const buttonIndex = parseInt(match[1]) - 1;
          const state = match[2].toLowerCase() === "on";
          setButtons((prevButtons) => prevButtons.map((btn, index) => (index === buttonIndex ? { ...btn, isActive: state } : btn)));
          console.log(`🟢 Button ${match[1]} set to ${state ? "ON" : "OFF"}`);
        }
      }
    });

    publishHandshake();
    return () => socket.disconnect();
  }, [deviceId]);

  // Fetch channels
  useEffect(() => {
    const url = `${API_URL}/get-channels/${deviceId}`;
    const fetchData = async () => {
      try {
        const res = await fetch(url);
        const json = await res.json();
        setChannels(json.data);
        setButtons(json.buttons);
        setIndicators(json.indicators);
        setPushButtons(json.pushButtons);
      } catch (err) {
        console.log("error", err);
      }
    };
    fetchData();
  }, [refreshData, deviceId]);

  // Fetch logs
  useEffect(() => {
    const url = `${API_URL}/get-device-data/${deviceId}?startDate=${startDate}&endDate=${endDate}`;
    const fetchData = async () => {
      try {
        const res = await fetch(url);
        const json = await res.json();
        const y = [...json.data].reverse();
        setFile(y);
      } catch (err) {
        console.log("error", err);
      }
    };
    fetchData();
  }, [refreshData, startDate, endDate, deviceId]);

  // Delete device data
  const deleteDeviceData = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will clear all device data!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`${API_URL}/delete-device-data/${deviceId}`);
          if (res.status === 200) {
            Swal.fire("Deleted!", "Data cleared.", "success");
            setRefreshData(true);
          }
        } catch (err) {
          console.log("error", err);
        }
      }
    });
  };

  // Prepare chart data
  const TableData = file.map((row) => Object.values(row ?? {}));
  const arr = [];
  const colors = ["rgb(255, 20, 147)", "rgb(255, 165, 0)", "rgb(106, 90, 205)", "rgb(50, 205, 50)", "rgb(165, 42, 42)", "rgb(255,0,255)"];

  async function chartData(channels) {
    try {
      for (let i = 0; i < channels.length; i++) {
        const firstCells = file.slice(0, 50);
        const cellValues = [];
        const lastUpdated = [];
        firstCells.forEach((el) => {
          const keyVal = Object.keys(el)[i + 3];
          cellValues.push(el[keyVal]);
          lastUpdated.push(el.time);
        });
        cellValues.reverse();
        lastUpdated.reverse();
        const obj = {
          label: channels[i].channelDisplayName || channels[i].channelName,
          data: cellValues,
          borderColor: colors[i % colors.length],
          backgroundColor: colors[i % colors.length],
          date: lastUpdated,
        };
        if (arr.length <= channels.length) arr.push(obj);
      }
    } catch (err) {
      console.log(err);
    }
  }

  const data = { labels, datasets: arr };
  chartData(channels);

  // 🔘 Push Button handler
  const handlePushButtonClick = async (pushButton) => {
    try {
      // ⏳ Waiting popup
      Swal.fire({
        title: '<span style="font-size:22px; display:flex; align-items:center; justify-content:center; gap:8px;">⏱️ Waiting for Device Response</span>',
        html: `<p class="mt-2">This will timeout in <b></b> seconds</p>`,
        timer: 10000,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
          const b = Swal.getHtmlContainer().querySelector("b");
          const interval = setInterval(() => {
            if (b && Swal.getTimerLeft()) {
              b.textContent = Math.ceil(Swal.getTimerLeft() / 1000);
            }
          }, 1000);
          Swal.willClose = () => clearInterval(interval);
        },
      });

      // 📡 Call backend API
      const res = await fetch(`${API_URL}/push-button`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deviceId,
          pushButtonName: pushButton.pushButtonName,
        }),
      });

      const data = await res.json();

      Swal.close(); // close waiting popup

      // ✅ SUCCESS
      if (res.ok && data.status) {
        Swal.fire({
          icon: "success",
          title: "✅ Device Is Online",
          timer: 2000,
          showConfirmButton: false,
        });
      }
      // ❌ FAILURE (Device not responding)
      else {
        Swal.fire({
          icon: "error",
          title: "❌ Device Is Offline",
        });
      }
    } catch (error) {
      Swal.close();
      console.error("Push Button Error:", error);
      Swal.fire({
        icon: "error",
        title: "❌ Error",
        text: "Something went wrong. Please try again.",
      });
    }
  };

  // Toggle handler with clock icon + waiting popup + ACK logic
  const handleToggleButton = async (btn) => {
    try {
      // Show waiting popup with timer and clock icon
      Swal.fire({
        title: '<span style="font-size: 24px; display: flex; align-items: center; justify-content: center; gap: 8px;">⏱️ Waiting for Device Response...</span>',
        html: `<div style="margin-top: 10px;">
              <p>This will close automatically in <b style="font-weight: 600; color: #007bff;"></b> seconds.</p>
            </div>`,
        timer: 10000,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
          const b = Swal.getHtmlContainer().querySelector("b");
          const interval = setInterval(() => {
            if (b && Swal.getTimerLeft()) {
              b.textContent = Math.ceil(Swal.getTimerLeft() / 1000);
            }
          }, 1000);
          Swal.willClose = () => clearInterval(interval);
        },
      });

      // Send request to backend
      const res = await fetch(`${API_URL}/update-button`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deviceId: btn.deviceId,
          buttonName: btn.buttonName,
          isActive: !btn.isActive,
        }),
      });

      const data = await res.json();

      Swal.close(); // close waiting popup

      // 🟢 Success if device responded (any message)
      if (res.ok && data.status) {
        setButtons((prev) => prev.map((b) => (b._id === btn._id ? { ...b, isActive: !b.isActive } : b)));

        Swal.fire({
          icon: "success",
          title: "Device acknowledged successfully!",
          timer: 2000,
          showConfirmButton: false,
        });
      }
      // ⚠️ Device Offline
      else if (!data.status && (data.message === "Your device is offline." || data.error === "No response from device")) {
        Swal.fire({
          icon: "warning",
          title: "⚠️ Device Offline",
          text: "No response received from device. Please check the connection.",
        });
      }
      // ❌ Any other error
      else {
        Swal.fire({
          icon: "error",
          title: "❌ Error",
          text: data.message || "Failed to update button.",
        });
      }
    } catch (err) {
      Swal.close();
      console.error("Toggle error:", err);
      Swal.fire({
        icon: "error",
        title: "❌ Error",
        text: "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <Sidebar
      element={
        <div className="container-fluid pe-5 pt-3">
          {/* Header */}
          <div className="row mt-5 mb-4">
            <div className="col-sm-6">
              <h3 className="blue-text font_weight">{`Device (${deviceId})`}</h3>
            </div>
            <div className="col-sm-6">
              <div className="ms-auto" style={{ width: "fit-content", display: "flex" }}>
                <Dropdown style={{ display: "inline-block" }}>
                  <Dropdown.Toggle className="btn-2" id="dropdown-basic">
                    Export
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => download("tbl")}>Export CSV</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                {user?.role !== "Dealer" && user?.role !== "user" && (
                  <NavLink to="/device-settings/" state={{ deviceId }} className="ms-2">
                    <button type="button" className="btn-2 submit-btn">
                      Settings
                    </button>
                  </NavLink>
                )}
              </div>
            </div>
          </div>

          {/* Channels */}
          <div className="row">
            {channels.map((ch, idx) => (
              <div key={idx} className="mb-4 col-12 col-md-4 col-lg-3">
                <div className="card p-2">
                  <div className="row g-0">
                    <div className="col-md-4 m-auto">
                      <img src={rain} style={{ width: "70%", margin: "auto", display: "block" }} className="img-fluid rounded-start mt-2" alt="..." />
                    </div>
                    <div className="col-md-8">
                      <div className="card-body">
                        <h5 className="card-text">
                          {ch.channelDisplayName || ch.channelName}: {ch.channelData}
                        </h5>
                        <p className="card-text">
                          <small className="text-muted">Last Updated: {calculateTimeDifference(ch.updatedAt)}</small>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="row my-4">
            <div className="col-12">
              <h3 className="font_weight mb-3">Device Controls</h3>
              <div className="d-flex flex-wrap gap-3">
                {buttons.length > 0 ? (
                  buttons.map((btn, index) => (
                    <div
                      key={index}
                      className="p-3 rounded shadow-sm"
                      style={{
                        backgroundColor: btn.isActive ? "#e0ffe0" : "#ffe0e0",
                        width: "250px",
                      }}
                    >
                      {/* fb1/fb2 indicator dynamic */}
                      <div className="d-flex justify-content-between align-items-center mb-2 btn-indicator-div">
                        <div className="d-flex align-items-center">
                          <span className="me-2">I 1</span>
                          <div className={`p-1 rounded-circle ${btn.fb1 === 1 ? "bg-success" : "bg-secondary"}`} style={{ width: "14px", height: "14px" }}></div>
                        </div>
                        <div className="d-flex align-items-center">
                          <span className="me-2">I 2</span>
                          <div className={`p-1 rounded-circle ${btn.fb2 === 1 ? "bg-success" : "bg-secondary"}`} style={{ width: "14px", height: "14px" }}></div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h6 className="mb-1">{btn.buttonDisplayName || `Button ${index + 1}`}</h6>
                          <small className="text-muted">{btn.isActive ? btn.activeMessage || "Active" : btn.inactiveMessage || "Inactive"}</small>
                        </div>
                        <label className="switch">
                          <input type="checkbox" checked={btn.isActive} onChange={() => handleToggleButton(btn)} />
                          <span className="slider round"></span>
                        </label>
                      </div>
                    </div>
                  ))
                ) : (
                  <></>
                )}
              </div>

              <div className="mt-4">
                <h3 className="font_weight mb-3">Indicators</h3>
                <div className="d-flex justify-content-start gap-4 flex-wrap align-items-center ">
                  {indicators && indicators.length > 0 ? (
                    indicators.map((indicator, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center p-2 rounded-3 bg-white"
                        style={{
                          boxShadow: "rgb(0 0 0 / 40%) 0px 0px 8px 0px",
                        }}
                      >
                        <span className="me-2">{indicator.indicatorDisplayName}</span>
                        <div className={`p-2 rounded-circle ${indicator.value === 1 ? "bg-success" : "bg-secondary"}`}></div>
                      </div>
                    ))
                  ) : (
                    <></>
                  )}
                </div>
              </div>
              {pushButtons && pushButtons.length > 0 && (
                <div className="mt-4">
                  <h3 className="font_weight mb-3">Push Buttons</h3>
                  <div className="d-flex justify-content-start gap-4 flex-wrap align-items-center ">
                    {pushButtons && pushButtons.length > 0 ? (
                      pushButtons.map((pushButton, index) => (
                        <div key={index} className="d-flex align-items-center p-2">
                          <button onClick={() => handlePushButtonClick(pushButton)} className="loading-button">
                            {pushButton.pushButtonDisplayName}
                          </button>
                        </div>
                      ))
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Chart */}
          <div className="row">
            <div className="col-12 mb-5">
              <div
                className="py-4 bg-white"
                style={{
                  borderRadius: "10px",
                  boxShadow: "rgb(0 0 0 / 40%) 0px 0px 8px 0px",
                }}
              >
                <div className="col-md-12 col-12 mx-auto" style={{ minHeight: "400px" }}>
                  <Line options={options} data={data} />
                </div>
              </div>
            </div>
          </div>

          {/* Logs */}
          <div className="row">
            <div className="col-12 mb-5">
              <div
                className="p-4"
                style={{
                  borderRadius: "10px",
                  boxShadow: "rgb(0 0 0 / 40%) 0px 0px 8px 0px",
                  backgroundColor: "white",
                }}
              >
                <div className="col-md-12 mx-auto">
                  <h3 className="font_weight">Device Logs</h3>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <p>Start Date</p>
                      <DatePicker className="input_3" showIcon selected={startDate} minDate={pastDate} maxDate={new Date()} onChange={(d) => setStartDate(d)} />
                    </div>
                    <div className="col-md-8">
                      <p>End Date</p>
                      <DatePicker className="input_3" showIcon selected={endDate} minDate={pastDate} maxDate={new Date()} onChange={(d) => setEndDate(d)} />
                    </div>
                  </div>
                  <div style={{ overflowY: "auto", maxHeight: "500px" }}>
                    <table className="table table-striped mb-0 text-center" id="tbl" style={{ borderCollapse: "collapse", width: "100%" }}>
                      <thead
                        style={{
                          position: "sticky",
                          top: "0px",
                          background: "#fff",
                        }}
                      >
                        <tr>
                          <th>Event Id</th>
                          <th>Device Id</th>
                          <th>Date</th>
                          {channels.map((c) => (
                            <th key={c.channelName}>{c.channelDisplayName || c.channelName}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {TableData.map((row, i) => (
                          <tr key={i}>
                            {row.map((cell, j) => (
                              <td key={j}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={deleteDeviceData} type="button" className="ms-auto mt-3 btn btn-danger submit-btn py-2 px-4">
                    Clear Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default Devices;
